// ./src/components/features/authentication/SignupForm/SignupForm.tsx
import React, { useState } from "react";
import {
  Stack,
  Button,
  TextInput,
  PasswordInput,
  Text,
  Anchor,
  Alert,
  Box,
  Group,
  Progress,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconAlertCircle,
  IconCheck,
  IconArrowRight,
} from "@tabler/icons-react";

import { useApp } from "@/contexts/AppContext";

interface SignupFormData {
  companyName: string;
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  companyName?: string;
  username?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  general?: string;
}

export interface SignupFormProps {
  onRegistrationSuccess?: (userData: any) => void;
  onNavigateToSignin?: () => void;
  showSigninLink?: boolean;
}

const getPasswordStrength = (password: string): number => {
  let strength = 0;
  if (password.length >= 8) strength += 25;
  if (password.length >= 12) strength += 10;
  if (/[a-z]/.test(password)) strength += 15;
  if (/[A-Z]/.test(password)) strength += 20;
  if (/\d/.test(password)) strength += 15;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 15;
  return Math.min(strength, 100);
};

const getStrengthColor = (strength: number, theme: any): string => {
  if (strength < 30) return theme.colors.error[5];
  if (strength < 60) return theme.colors.warning[5];
  return theme.colors.success[5];
};

export const SignupForm: React.FC<SignupFormProps> = ({
  onRegistrationSuccess,
  onNavigateToSignin,
  showSigninLink = true,
}) => {
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<FormErrors>({});
  const [successMessage, setSuccessMessage] = useState<string>("");
  const { signUpWithCredentials } = useApp();

  const form = useForm<SignupFormData>({
    initialValues: {
      companyName: "",
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    validate: {
      companyName: (value) => {
        if (!value) return "Required";
        if (value.length < 2) return "Min 2 characters";
        return null;
      },
      username: (value) => {
        if (!value) return "Required";
        if (value.length < 3) return "Min 3 characters";
        if (!/^[a-zA-Z0-9_-]+$/.test(value))
          return "Letters, numbers, - and _ only";
        return null;
      },
      email: (value) => {
        if (!value) return "Required";
        if (!/^\S+@\S+\.\S+$/.test(value)) return "Invalid email";
        return null;
      },
      password: (value) => {
        if (!value) return "Required";
        if (value.length < 8) return "Min 8 characters";
        if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value))
          return "Need uppercase, lowercase & number";
        return null;
      },
      confirmPassword: (value, values) => {
        if (!value) return "Required";
        if (value !== values.password) return "Passwords don't match";
        return null;
      },
    },
  });

  const handleSubmit = async (values: SignupFormData) => {
    setLoading(true);
    setServerErrors({});
    setSuccessMessage("");

    try {
      const result = await signUpWithCredentials({
        companyName: values.companyName,
        username: values.username,
        email: values.email,
        password: values.password,
        repeatPassword: values.confirmPassword,
      });

      if (result.didSucceed()) {
        setSuccessMessage("Account created! Redirecting...");
        setTimeout(() => onRegistrationSuccess?.(result.getDataFirst()), 1000);
      } else {
        throw Error(result.getErrorSummary());
      }
    } catch (error) {
      setServerErrors({
        general:
          error instanceof Error
            ? error.message
            : "Registration failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(form.values.password);

  // Success state
  if (successMessage) {
    return (
      <Box
        p="md"
        style={{
          backgroundColor: theme.colors.success[0],
          borderRadius: theme.radius.md,
          border: `1px solid ${theme.colors.success[2]}`,
        }}
      >
        <Group gap="sm" wrap="nowrap" align="center">
          <Box
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: theme.colors.success[5],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <IconCheck size={20} color="white" />
          </Box>
          <Text size="sm" fw={500} c={theme.colors.success[7]}>
            {successMessage}
          </Text>
        </Group>
      </Box>
    );
  }

  const inputStyles = {
    label: { fontSize: "0.8125rem", fontWeight: 500, marginBottom: 4 },
    input: { fontSize: "0.9375rem" },
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="xs">
        {serverErrors.general && (
          <Alert
            color="red"
            icon={<IconAlertCircle size={16} />}
            radius="md"
            py="xs"
            styles={{
              root: {
                backgroundColor: theme.colors.error[0],
                border: `1px solid ${theme.colors.error[2]}`,
              },
              message: { color: theme.colors.error[7], fontSize: "0.875rem" },
            }}
          >
            {serverErrors.general}
          </Alert>
        )}

        {/* Two-column layout for company and username on larger screens */}
        <TextInput
          label="Company"
          placeholder="Company name"
          radius="md"
          size="sm"
          {...form.getInputProps("companyName")}
          error={form.errors.companyName || serverErrors.companyName}
          styles={inputStyles}
        />

        <TextInput
          label="Username"
          placeholder="Choose username"
          radius="md"
          size="sm"
          {...form.getInputProps("username")}
          error={form.errors.username || serverErrors.username}
          styles={inputStyles}
        />

        <TextInput
          label="Email"
          placeholder="you@company.com"
          radius="md"
          size="sm"
          type="email"
          {...form.getInputProps("email")}
          error={form.errors.email || serverErrors.email}
          styles={inputStyles}
        />

        <Box>
          <PasswordInput
            label="Password"
            placeholder="Create password"
            radius="md"
            size="sm"
            {...form.getInputProps("password")}
            error={form.errors.password || serverErrors.password}
            styles={inputStyles}
          />
          {form.values.password && (
            <Progress
              value={passwordStrength}
              color={getStrengthColor(passwordStrength, theme)}
              size={4}
              radius="xl"
              mt={6}
            />
          )}
        </Box>

        <PasswordInput
          label="Confirm Password"
          placeholder="Re-enter password"
          radius="md"
          size="sm"
          {...form.getInputProps("confirmPassword")}
          error={form.errors.confirmPassword || serverErrors.confirmPassword}
          styles={inputStyles}
        />

        <Button
          type="submit"
          fullWidth
          size="md"
          radius="md"
          loading={loading}
          mt="xs"
          rightSection={!loading && <IconArrowRight size={16} />}
          styles={{
            root: {
              height: 44,
              fontWeight: 600,
              background: `linear-gradient(135deg, ${theme.colors.brand[6]} 0%, ${theme.colors.brand[5]} 100%)`,
              "&:hover": { boxShadow: theme.other.shadowBrand },
            },
          }}
        >
          Create Account
        </Button>

        {showSigninLink && (
          <Text size="sm" ta="center" c={theme.colors.neutral[5]}>
            Already have an account?{" "}
            <Anchor
              onClick={(e) => {
                e.preventDefault();
                onNavigateToSignin?.();
              }}
              fw={600}
              c={theme.colors.brand[5]}
            >
              Sign in
            </Anchor>
          </Text>
        )}
      </Stack>
    </form>
  );
};

export default SignupForm;
